import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wrench, Camera, RotateCcw, Trash2, Loader2, Bot } from "lucide-react";
import { HowItWorks, PageHeader } from "@/components/kit";
import { maintenance as maintApi, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Maintenance() {
  const { refresh } = useData();
  const [snaps, setSnaps] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // snapshot da ripristinare
  const [delTarget, setDelTarget] = useState(null); // snapshot da eliminare
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => { try { setSnaps(await maintApi.list()); } catch (e) { toast.error(apiError(e)); setSnaps([]); } };
  useEffect(() => { load(); }, []);

  const createSnap = async () => {
    setBusy(true);
    try { const d = await maintApi.create(name || undefined); toast.success(`Punto di ripristino creato (${d.roleCount} ruoli)`); setName(""); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const applySnap = async () => {
    if (!confirm) return;
    setApplying(true);
    try {
      const res = await maintApi.apply(confirm.id, deleteMissing);
      toast.success(`Ruoli ripristinati (${res.actions?.length || 0} azioni)`);
      if (res.errors?.length) toast.error(`${res.errors.length} errori — controlla la gerarchia del bot`);
      setConfirm(null); setDeleteMissing(false);
      await refresh(); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setApplying(false); }
  };
  const deleteSnap = async (id) => {
    try { await maintApi.remove(id); await load(); toast.success("Punto di ripristino eliminato"); }
    catch (e) { toast.error(apiError(e)); } finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={Wrench} title="Punti di ripristino dei ruoli"
        subtitle="Salva com'erano i ruoli prima di cambiarli: se qualcosa va storto, torni indietro con un clic." />

      <HowItWorks id="maintenance"
        intro="Funziona come il «punto di ripristino» di Windows, ma solo per i ruoli del server (nomi, colori, permessi, ordine)."
        steps={[
          <>Prima di toccare i ruoli premi <b className="text-white">Crea punto di ripristino</b> (dagli un nome, es. «prima di riorganizzare lo staff»).</>,
          <>Fai tutte le modifiche che vuoi nella pagina <b className="text-white">Ruoli</b>.</>,
          <>Se qualcosa non va (qualcuno ha perso permessi, ruoli sbagliati), torna qui e premi <b className="text-white">Ripristina</b>: i ruoli tornano esattamente com'erano.</>,
        ]}
        tips={[
          "Prima di ogni ripristino il pannello salva da solo anche lo stato attuale, così puoi annullare anche il ripristino.",
          "Differenza con la pagina Backup: il Backup salva tutto il server (anche canali e permessi dei canali); qui solo i ruoli, ed è più rapido.",
          "I ruoli dei bot e quelli sopra al ruolo del tuo bot non si possono modificare (limite di Discord).",
        ]} />

      <Card className="border-[#232838] bg-[#161923] p-4">
        <p className="mb-2 text-sm font-semibold text-white">Nuovo punto di ripristino</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome (facoltativo) — es. «prima riorganizzazione staff»" data-testid="snapshot-name-input" className="border-[#131620] bg-[#131620] text-white" />
          <Button onClick={createSnap} disabled={busy} data-testid="create-snapshot-btn" className="bg-[#5865F2] font-semibold hover:bg-[#4752C4]">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}Crea punto di ripristino
          </Button>
        </div>
      </Card>

      <Card className="border-[#232838] bg-[#161923] p-4">
        <p className="mb-3 text-sm font-semibold text-white">Punti di ripristino salvati</p>
        <div className="space-y-2" data-testid="snapshots-list">
          {snaps === null && <p className="text-sm text-[#949BA4]">Caricamento…</p>}
          {snaps && snaps.length === 0 && <p className="text-sm text-[#949BA4]">Nessun punto di ripristino. Creane uno prima di modificare i ruoli.</p>}
          {snaps && snaps.map((sn) => (
            <div key={sn.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-[#131620] px-3 py-2.5" data-testid={`snapshot-row-${sn.id}`}>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${sn.auto ? "bg-[#FEE75C]/20 text-[#FEE75C]" : "bg-[#5865F2] text-white"}`}>{sn.auto ? "AUTO" : "MANUALE"}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{sn.name}</p>
                <p className="text-xs text-[#949BA4]">{sn.at ? new Date(sn.at).toLocaleString("it-IT") : ""} · {sn.roleCount} ruoli · {sn.by}</p>
              </div>
              <Button size="sm" onClick={() => { setConfirm(sn); setDeleteMissing(false); }} data-testid={`snapshot-restore-${sn.id}`} className="bg-[#248046] hover:bg-[#1a6334]"><RotateCcw className="mr-1 h-4 w-4" />Ripristina</Button>
              <Button size="icon" variant="ghost" onClick={() => setDelTarget(sn)} data-testid={`snapshot-delete-${sn.id}`} className="text-[#F23F43] hover:bg-[#F23F43]/15"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Ripristinare i ruoli?</DialogTitle></DialogHeader>
          {confirm && (
            <>
              <p className="text-sm text-slate-300">I ruoli torneranno come nel punto <b>«{confirm.name}»</b> ({confirm.roleCount} ruoli). Prima viene salvato in automatico anche lo stato attuale, così puoi tornare indietro.</p>
              <label className="flex items-center gap-2 rounded-lg border border-[#F23F43]/30 bg-[#F23F43]/10 p-3 text-sm text-[#f7a5a7]">
                <Switch checked={deleteMissing} onCheckedChange={setDeleteMissing} data-testid="restore-delete-missing" />
                Elimina anche i ruoli creati dopo questo punto (irreversibile)
              </label>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirm(null)} className="text-slate-400">Annulla</Button>
                <Button onClick={applySnap} disabled={applying} data-testid="restore-confirm-btn" className="bg-[#248046] hover:bg-[#1a6334]">
                  {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bot className="mr-2 h-4 w-4" />}Applica al server
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Eliminare il punto di ripristino?</DialogTitle></DialogHeader>
          {delTarget && <p className="text-sm text-slate-300">Il punto di ripristino <b>«{delTarget.name}»</b> ({delTarget.roleCount} ruoli) verrà eliminato definitivamente.</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)} className="text-slate-400">Annulla</Button>
            <Button onClick={() => deleteSnap(delTarget.id)} data-testid="snapshot-delete-confirm-btn" className="bg-[#DA373C] hover:bg-[#a12828]"><Trash2 className="mr-2 h-4 w-4" />Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
